
import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, Image as ImageIcon, Save, Check, Instagram, Facebook, Linkedin, FileText, Eye, EyeOff } from 'lucide-react';
import { uploadLogo, saveBrandingSettings, uploadBrandLogo } from '../services/firebase';
import { AppSettings, BrandLink, SocialLinks } from '../types';

interface BrandingSettingsProps {
  currentSettings: AppSettings;
  onUpdate: (newSettings: AppSettings) => void;
}

const Toggle = ({ label, checked, onChange, helpText }: { label: string, checked: boolean, onChange: (v: boolean) => void, helpText?: string }) => (
  <div className="flex items-center justify-between py-2 gap-4">
    <div className="flex-1 pr-4">
        <span className="text-sm font-bold text-dark block">{label}</span>
        {helpText && <p className="text-xs text-cool mt-0.5 leading-tight">{helpText}</p>}
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-dark focus:ring-offset-2 ${checked ? 'bg-dark' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

const BrandingSettings: React.FC<BrandingSettingsProps> = ({ currentSettings, onUpdate }) => {
  const [logoPreview, setLogoPreview] = useState<string | undefined>(currentSettings.logoUrl);
  const [file, setFile] = useState<File | null>(null);
  const [companyName, setCompanyName] = useState(currentSettings.companyName || '');
  const [publicNoticeBanner, setPublicNoticeBanner] = useState(currentSettings.publicNoticeBanner || '');
  
  // Social Links
  const [socials, setSocials] = useState<SocialLinks>(currentSettings.socialLinks || { instagram: '', facebook: '', linkedin: '', pinterest: '' });
  const [showSocials, setShowSocials] = useState<boolean>(currentSettings.showSocials ?? true);

  // Legal
  const [privacyPolicy, setPrivacyPolicy] = useState(currentSettings.privacyPolicy || '');
  const [termsOfService, setTermsOfService] = useState(currentSettings.termsOfService || '');

  // Footer Brands (Initialize with 4 empty slots or existing data)
  const initialBrands: BrandLink[] = currentSettings.footerBrands && currentSettings.footerBrands.length === 4 
    ? currentSettings.footerBrands 
    : [
        { name: 'Esselle Retail', url: '', logoUrl: '', visible: true },
        { name: 'Esselle Home', url: '', logoUrl: '', visible: true },
        { name: 'Arthouse', url: '', logoUrl: '', visible: true },
        { name: 'Belfield Home', url: '', logoUrl: '', visible: true },
      ];
  
  const [brands, setBrands] = useState<BrandLink[]>(initialBrands);
  const [brandFiles, setBrandFiles] = useState<{ [key: number]: File }>({});
  const [brandPreviews, setBrandPreviews] = useState<{ [key: number]: string }>({});

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    setLogoPreview(currentSettings.logoUrl);
    setCompanyName(currentSettings.companyName || '');
    setPublicNoticeBanner(currentSettings.publicNoticeBanner || '');
    setSocials(currentSettings.socialLinks || { instagram: '', facebook: '', linkedin: '', pinterest: '' });
    setShowSocials(currentSettings.showSocials ?? true);
    setPrivacyPolicy(currentSettings.privacyPolicy || '');
    setTermsOfService(currentSettings.termsOfService || '');
    if (currentSettings.footerBrands && currentSettings.footerBrands.length === 4) {
        // Ensure visible property exists
        const safeBrands = currentSettings.footerBrands.map(b => ({
            ...b,
            visible: b.visible ?? true
        }));
        setBrands(safeBrands);
    }
  }, [currentSettings]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(selected);
    }
  };

  const handleBrandFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const selected = e.target.files[0];
          setBrandFiles(prev => ({ ...prev, [index]: selected }));
          
          const reader = new FileReader();
          reader.onloadend = () => {
              setBrandPreviews(prev => ({ ...prev, [index]: reader.result as string }));
          };
          reader.readAsDataURL(selected);
      }
  };

  const updateBrand = (index: number, field: keyof BrandLink, value: any) => {
      const newBrands = [...brands];
      newBrands[index] = { ...newBrands[index], [field]: value };
      setBrands(newBrands);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
        let finalLogoUrl = logoPreview;

        // 1. Upload Main Logo
        if (file) {
            finalLogoUrl = await uploadLogo(file);
        }

        // 2. Upload Footer Brand Logos
        const updatedBrands = await Promise.all(brands.map(async (brand, index) => {
            const newFile = brandFiles[index];
            if (newFile) {
                const url = await uploadBrandLogo(newFile);
                return { ...brand, logoUrl: url };
            }
            return brand;
        }));

        const newSettings: AppSettings = {
            logoUrl: finalLogoUrl,
            companyName,
            publicNoticeBanner,
            socialLinks: socials,
            showSocials,
            footerBrands: updatedBrands,
            privacyPolicy,
            termsOfService
        };

        await saveBrandingSettings(newSettings);

        onUpdate(newSettings);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
        console.error(e);
        alert('Failed to save settings.');
    } finally {
        setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-12">
        <h1 className="font-serif text-3xl text-dark mb-2">Branding & Footer</h1>
        <p className="text-cool mb-8">Customize your viewer identity, social links, and partner brands.</p>

        <div className="space-y-8">
            {/* Main Logo Section */}
            <div className="bg-white rounded-xl shadow-sm border border-warm/20 p-6 md:p-8">
                <h2 className="font-serif text-xl text-dark mb-6">Main Identity</h2>
                
                <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-48 h-48 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center relative overflow-hidden group flex-shrink-0">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Logo Preview" className="w-auto h-auto max-w-[80%] max-h-[80%] object-contain" />
                        ) : (
                            <ImageIcon className="text-gray-300 w-12 h-12" />
                        )}
                        <div className="absolute inset-0 bg-dark/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                className="text-white text-sm font-semibold hover:underline"
                            >
                                Change
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 space-y-4 w-full">
                        <div 
                            onClick={() => fileInputRef.current?.click()}
                            className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                            <div className="flex items-center gap-4">
                                <div className="bg-warm/10 p-3 rounded-full text-dark">
                                    <UploadCloud size={24} />
                                </div>
                                <div>
                                    <h4 className="font-medium text-dark">Upload Main Logo</h4>
                                    <p className="text-sm text-cool">Min height 120px for retina displays. PNG/SVG recommended.</p>
                                </div>
                            </div>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-2">Company Name (Alt Text)</label>
                            <input 
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-dark outline-none transition-all mb-4"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-2">Public Notice Banner</label>
                            <input 
                                type="text"
                                value={publicNoticeBanner}
                                onChange={(e) => setPublicNoticeBanner(e.target.value)}
                                placeholder="e.g. Free shipping on orders over £50"
                                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-dark outline-none transition-all"
                            />
                            <p className="text-[10px] text-cool mt-1">Displayed at the top of the public library page.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white rounded-xl shadow-sm border border-warm/20 p-6 md:p-8">
                 <div className="flex justify-between items-center mb-6">
                    <h2 className="font-serif text-xl text-dark">Social Media Links</h2>
                    <Toggle 
                        label="Show Social Links" 
                        checked={showSocials} 
                        onChange={setShowSocials} 
                    />
                 </div>
                 
                 <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity duration-300 ${!showSocials ? 'opacity-40 pointer-events-none' : ''}`}>
                     <div>
                         <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-2">Instagram URL</label>
                         <div className="relative">
                            <Instagram size={18} className="absolute left-3 top-2.5 text-cool" />
                            <input 
                                type="text" 
                                value={socials.instagram} 
                                onChange={(e) => setSocials({...socials, instagram: e.target.value})}
                                placeholder="https://instagram.com/..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:border-dark outline-none"
                            />
                         </div>
                     </div>
                     <div>
                         <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-2">Facebook URL</label>
                         <div className="relative">
                            <Facebook size={18} className="absolute left-3 top-2.5 text-cool" />
                            <input 
                                type="text" 
                                value={socials.facebook} 
                                onChange={(e) => setSocials({...socials, facebook: e.target.value})}
                                placeholder="https://facebook.com/..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:border-dark outline-none"
                            />
                         </div>
                     </div>
                     <div>
                         <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-2">LinkedIn URL</label>
                         <div className="relative">
                            <Linkedin size={18} className="absolute left-3 top-2.5 text-cool" />
                            <input 
                                type="text" 
                                value={socials.linkedin} 
                                onChange={(e) => setSocials({...socials, linkedin: e.target.value})}
                                placeholder="https://linkedin.com/in/..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:border-dark outline-none"
                            />
                         </div>
                     </div>
                     <div>
                         <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-2">Pinterest URL</label>
                         <div className="relative">
                            <span className="absolute left-3 top-2.5 text-cool font-bold text-sm">P</span>
                            <input 
                                type="text" 
                                value={socials.pinterest} 
                                onChange={(e) => setSocials({...socials, pinterest: e.target.value})}
                                placeholder="https://pinterest.com/..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:border-dark outline-none"
                            />
                         </div>
                     </div>
                 </div>
            </div>

            {/* Legal Information Section */}
            <div className="bg-white rounded-xl shadow-sm border border-warm/20 p-6 md:p-8">
                 <h2 className="font-serif text-xl text-dark mb-6">Legal Information</h2>
                 <p className="text-sm text-cool mb-6">Paste your policy text here. It will be displayed in a popup on the public library footer.</p>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                             <FileText size={16} className="text-warm" />
                             <label className="text-xs font-semibold text-cool uppercase tracking-wide">Privacy Policy</label>
                        </div>
                        <textarea 
                            value={privacyPolicy}
                            onChange={(e) => setPrivacyPolicy(e.target.value)}
                            className="w-full h-64 px-4 py-3 border border-gray-200 rounded-md focus:border-dark outline-none resize-none text-sm text-cool"
                            placeholder="Paste Privacy Policy text here..."
                        />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                             <FileText size={16} className="text-warm" />
                             <label className="text-xs font-semibold text-cool uppercase tracking-wide">Terms of Service</label>
                        </div>
                        <textarea 
                            value={termsOfService}
                            onChange={(e) => setTermsOfService(e.target.value)}
                            className="w-full h-64 px-4 py-3 border border-gray-200 rounded-md focus:border-dark outline-none resize-none text-sm text-cool"
                            placeholder="Paste Terms of Service text here..."
                        />
                    </div>
                 </div>
            </div>

            {/* Footer Brands Section */}
            <div className="bg-white rounded-xl shadow-sm border border-warm/20 p-6 md:p-8">
                 <h2 className="font-serif text-xl text-dark mb-6">Footer Partner Brands</h2>
                 <p className="text-sm text-cool mb-6">Manage the 4 brand links displayed on the bottom right of the public library. Toggle visibility to hide unused slots.</p>
                 
                 <div className="space-y-6">
                    {brands.map((brand, idx) => (
                        <div key={idx} className={`flex flex-col md:flex-row gap-6 p-4 border rounded-lg transition-colors ${brand.visible ? 'border-gray-200 bg-gray-50/50' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                            {/* Logo Upload Mini */}
                            <div className="flex-shrink-0">
                                <label className="block text-[10px] font-bold text-cool uppercase mb-2">Logo</label>
                                <div className="w-24 h-24 bg-white border border-dashed border-gray-300 rounded flex items-center justify-center relative group overflow-hidden">
                                     {brandPreviews[idx] || brand.logoUrl ? (
                                         <img src={brandPreviews[idx] || brand.logoUrl} className="w-full h-full object-contain p-1" alt="" />
                                     ) : (
                                         <span className="text-xs text-cool/50">No Logo</span>
                                     )}
                                     <input type="file" className="absolute inset-0 opacity-0 cursor-pointer z-10" accept="image/*" onChange={(e) => handleBrandFileChange(idx, e)} />
                                     <div className="absolute inset-0 bg-black/40 hidden group-hover:flex items-center justify-center text-white text-[10px] font-bold uppercase tracking-wider">Change</div>
                                </div>
                            </div>

                            <div className="flex-1 space-y-4">
                                <div className="flex items-start justify-between">
                                    <h4 className="font-bold text-dark text-sm">Brand Slot {idx + 1}</h4>
                                    <Toggle 
                                        label={brand.visible ? "Visible" : "Hidden"} 
                                        checked={!!brand.visible} 
                                        onChange={(v) => updateBrand(idx, 'visible', v)} 
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-cool uppercase mb-1">Brand Name / Alt Text</label>
                                        <input 
                                            type="text" 
                                            value={brand.name}
                                            onChange={(e) => updateBrand(idx, 'name', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-dark outline-none"
                                            placeholder={`Brand ${idx + 1}`}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-cool uppercase mb-1">Website URL</label>
                                        <input 
                                            type="text" 
                                            value={brand.url}
                                            onChange={(e) => updateBrand(idx, 'url', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-dark outline-none"
                                            placeholder="https://..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                 </div>
            </div>

            <div className="pt-6 flex justify-end">
                <button 
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all shadow-lg ${
                        success 
                        ? 'bg-green-600 text-white' 
                        : 'bg-dark text-white hover:bg-cool'
                    }`}
                >
                    {saving ? 'Saving...' : success ? <><Check size={18} /> Saved</> : <><Save size={18} /> Save Settings</>}
                </button>
            </div>
        </div>
    </div>
  );
};

export default BrandingSettings;
